import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog } from "antd-mobile";

import {
  fetchCarOrderDetail,
  cancelCarOrder,
  type CarOrderDetailResponse,
} from "@/api/car";
import { REFUND_STATUS_MAP, type RefundInfo } from "@/api/order";
import { cancelCarRefund } from "@/api/afterSale";
import { toastError, toastSuccess } from "@/utils/toast";
import ProductActionButtons from "../components/ProductActionButtons";
import InvoiceStatusNotice from "../components/InvoiceStatusNotice";
import VoucherServiceSection from "../components/VoucherServiceSection";
import CarInspectionSection from "../hotel/sections/CarInspectionSection";
import { AfterSaleModal, ReviewModal, InvoiceModal } from "@/components/modals";

import CarOrderHeader from "./sections/CarOrderHeader";
import CarContractReminder from "./sections/CarContractReminder";
import CarVehicleCard from "./sections/CarVehicleCard";
import CarRenterSection from "./sections/CarRenterSection";
import CarDriverSection from "./sections/CarDriverSection";
import CarConfigSection from "./sections/CarConfigSection";
import CarFeeSection from "./sections/CarFeeSection";
import { photosFromBlock, resolveCarMainProgressStep } from "./utils";
import RefundInfoSection from "../components/RefundInfoSection";

type CarOrderData = CarOrderDetailResponse["data"];

type CarRefundInfo = CarOrderData["refund_info"];

function mapCarRefundInfoForSection(info: CarRefundInfo): RefundInfo {
  const imgs = (info as { apply_images?: string[] }).apply_images ?? [];
  return {
    has_refund: info.has_refund,
    refund_id: info.refund_id,
    refund_no: info.refund_no,
    refund_status: info.refund_status,
    refund_status_text: info.refund_status_text,
    apply_desc: info.apply_desc,
    apply_img: imgs,
    apply_reason: info.apply_reason,
    create_time: info.create_time,
    refund_money: String(info.refund_money ?? ""),
    refuse_desc: info.refuse_desc ?? "",
  };
}

export default function OrderDetailCarPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locState = location.state as {
    orderId?: number;
    openContract?: boolean;
  } | null;
  const orderId = locState?.orderId;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CarOrderData | null>(null);
  const [modal, setModal] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    fetchCarOrderDetail(orderId)
      .then(setData)
      .catch((err) => {
        console.error(err);
        toastError(err?.message || "加载失败");
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  const carProgressStep = useMemo(() => {
    if (!data) return 0;
    const hasRefund = (data as { has_refund?: number }).has_refund === 1;
    return resolveCarMainProgressStep(
      data.status,
      hasRefund,
      data.refund_info?.refund_status ?? 0,
    );
  }, [data]);

  const renter = useMemo(() => {
    const list = data?.travelers ?? [];
    return (
      list.find((t) => (t.license_number || "").length > 0) ?? list[0] ?? null
    );
  }, [data]);

  const pickupPhotos = useMemo(
    () => (data?.pickup_photos ? photosFromBlock(data.pickup_photos) : []),
    [data],
  );
  const returnPhotos = useMemo(
    () => (data?.return_photos ? photosFromBlock(data.return_photos) : []),
    [data],
  );

  const gearboxText = data?.car?.gearbox === 1 ? "自动" : "手动";

  const handleNavigate = (path: string) => {
    if (path === "/payment" && data) {
      navigate("/payment", {
        state: {
          orderNo: data.order_no,
          totalAmount: String(data.fee?.total_amount ?? 0),
          type: "car",
        },
      });
    } else {
      navigate(path);
    }
  };

  const handleAction = (action: string) => {
    if (action === "cancelRefund") {
      const rid = data?.refund_info?.refund_id;
      if (rid == null || rid === 0) {
        toastError("缺少售后单信息");
        return;
      }
      Dialog.show({
        content: "确认要取消该售后申请吗？",
        closeOnAction: true,
        actions: [
          [
            {
              key: "cancel",
              text: "再想想",
              style: { color: "#FF6B35", fontWeight: 500 },
              onClick: () => {},
            },
            {
              key: "confirm",
              text: "确认取消",
              bold: true,
              style: { color: "#FF6B35", fontWeight: 600 },
              onClick: async () => {
                try {
                  await cancelCarRefund(rid);
                  toastSuccess("已取消售后申请");
                  window.location.reload();
                } catch (e: unknown) {
                  toastError(e instanceof Error ? e.message : "取消失败");
                }
              },
            },
          ],
        ],
      });
      return;
    }
    if (
      action === "cancel" ||
      action === "cancel-free" ||
      action === "cancel-charged"
    ) {
      Dialog.show({
        content: `确认取消订单 ${data?.order_no ?? ""}？取消后无法恢复。`,
        closeOnAction: true,
        actions: [
          [
            {
              key: "cancel",
              text: "再想想",
              style: { color: "#FF6B35", fontWeight: 500 },
              onClick: () => {},
            },
            {
              key: "confirm",
              text: "确认取消",
              bold: true,
              style: { color: "#FF6B35", fontWeight: 600 },
              onClick: async () => {
                try {
                  await cancelCarOrder(orderId!, "用户主动取消");
                  toastSuccess("订单已取消");
                  window.location.reload();
                } catch (err: unknown) {
                  toastError(err instanceof Error ? err.message : "取消失败");
                }
              },
            },
          ],
        ],
      });
      return;
    }
    setModal(action);
  };

  if (!orderId) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: "#F4F5F9" }}
      >
        <p className="text-gray-500 text-sm text-center">
          缺少订单参数，请从订单列表进入
        </p>
      </div>
    );
  }

  if (!data && loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#F4F5F9" }}
      >
        <div className="flex flex-col items-center gap-3">
          <i
            className="ri-loader-4-line animate-spin"
            style={{ fontSize: 32, color: "#FF6B35" }}
          />
          <span className="text-gray-400">加载中...</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-3 px-6"
        style={{ background: "#F4F5F9" }}
      >
        <p className="text-gray-400 text-sm">无法加载订单信息</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-6 py-2.5 rounded-full text-sm font-medium cursor-pointer"
          style={{ background: "#FF6B35", color: "#fff" }}
        >
          返回
        </button>
      </div>
    );
  }

  const totalNum = Number(data.fee?.total_amount ?? 0);
  const hasComment = (data as { has_comment?: number }).has_comment ?? 0;
  const hasRefund = (data as { has_refund?: number }).has_refund ?? 0;
  const refundStatus = (data as { refund_status?: number }).refund_status;
  const hasInvoice = (data as { has_invoice?: number }).has_invoice ?? 0;
  const invoiceStatus = (
    data as { invoice_status?: number | string | null }
  ).invoice_status;
  const refundInfo = data.refund_info;
  /** 有售后且未取消(30)时，与列表/酒店/线路一致用统一售后文案 */
  const headerStatusText =
    refundInfo?.has_refund === 1 && refundInfo.refund_status !== 30
      ? (REFUND_STATUS_MAP[refundInfo.refund_status] ?? data.status_text)
      : data.status_text;

  const showRefundSection =
    refundInfo &&
    refundInfo.has_refund === 1 &&
    refundInfo.refund_status != null &&
    refundInfo.refund_status !== 0 &&
    refundInfo.refund_status !== 30;

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+112px)]"
      style={{
        background: "#F4F5F9",
        fontFamily: "'Inter','SF Pro Text',system-ui,sans-serif",
      }}
    >
      <CarOrderHeader
        orderNo={data.order_no}
        createdAt={data.created_at}
        totalAmount={totalNum}
        statusText={headerStatusText}
        statusDesc={data.status_desc}
        carProgressStep={carProgressStep}
        onBack={() => navigate(-1)}
      />

      <CarContractReminder
        visible={data.status === 2 || Boolean(locState?.openContract)}
      />

      <InvoiceStatusNotice
        hasInvoice={hasInvoice}
        invoiceStatus={invoiceStatus}
      />

      <CarVehicleCard data={data} />

      {renter ? <CarRenterSection renter={renter} /> : null}

      <CarDriverSection driver={data.driver} />

      <CarConfigSection car={data.car} gearboxText={gearboxText} />

      {pickupPhotos.length > 0 && (
        <div className="mt-3">
          <CarInspectionSection
            title="取车验车照片"
            color="#059669"
            photos={pickupPhotos}
          />
        </div>
      )}
      {returnPhotos.length > 0 && (
        <div className="mt-3">
          <CarInspectionSection
            title="还车验车照片"
            color="#2563EB"
            photos={returnPhotos}
          />
        </div>
      )}

      <CarFeeSection fee={data.fee} insurance={data.insurance} />

      {showRefundSection && refundInfo ? (
        <RefundInfoSection
          refundInfo={mapCarRefundInfoForSection(refundInfo)}
        />
      ) : null}

      <VoucherServiceSection
        onNavigate={(path) => handleNavigate(path)}
        items={[
          {
            icon: "ri-file-text-line",
            label: "电子合同",
            sub: "待签署",
            color: "#2563EB",
            path: "/contracts",
          },
          {
            icon: "ri-bill-line",
            label: "电子发票",
            sub: "可申请开具",
            color: "#D97706",
            path: "/invoices",
          },
          {
            icon: "ri-customer-service-2-line",
            label: "在线客服",
            sub: "7×24小时",
            color: "#7C3AED",
            path: "/",
          },
        ]}
      />

      <div
        className="fixed bottom-0 left-0 right-0 bg-white z-40 flex gap-3"
        style={{ borderTop: "1px solid #F5F5F5", paddingTop: "8px", paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))" }}
      >
        <ProductActionButtons
          productType="car"
          mainStatus={headerStatusText}
          mainStatusStep={carProgressStep}
          hasRefund={hasRefund}
          refundStatus={refundStatus}
          hasInvoice={hasInvoice}
          hasComment={hasComment}
          onAction={handleAction}
          onNavigate={handleNavigate}
          onReorder={() => {
            navigate("/car-detail-product", {
              state: { carId: data.car.id },
            });
          }}
        />
      </div>

      {modal === "afterSale" && (
        <AfterSaleModal
          visible
          onClose={() => setModal(null)}
          productName={data.car.title}
          orderId={orderId}
          orderType="car"
          orderAmount={totalNum}
          onSubmitSuccess={() => {
            setModal(null);
            if (orderId) {
              fetchCarOrderDetail(orderId)
                .then(setData)
                .catch(() => {});
            }
          }}
        />
      )}
      {modal === "review" && (
        <ReviewModal
          visible
          onClose={() => setModal(null)}
          orderId={orderId}
          productName={data.car.title}
          productId={data.car.id}
          productType="car"
          onSubmitSuccess={() => {
            setModal(null);
            if (orderId) {
              fetchCarOrderDetail(orderId)
                .then(setData)
                .catch(() => {});
            }
          }}
        />
      )}
      {modal === "invoice" && orderId != null && (
        <InvoiceModal
          orderId={orderId}
          productType="car"
          productTitle={data.car.title}
          productSubtitle={[
            [data.car.brand, data.car.model].filter(Boolean).join(" "),
            data.car.seats_text,
          ]
            .filter(Boolean)
            .join(" · ")}
          invoiceAmount={totalNum}
          onClose={() => setModal(null)}
          onSubmitSuccess={() => setModal(null)}
        />
      )}
    </div>
  );
}
